import { addToCart } from '@theme/pj-cart';

/**
 * Politics Jeans product page.
 * - Resolves the variant from option inputs (case-insensitive, so "s" and "S" match)
 * - Sold-out selection turns the main button into "Notify me"
 * - Notify me uses Klaviyo's back-in-stock trigger when its embed provides one,
 *   otherwise opens the built-in contact-form fallback
 */
class PjProduct extends HTMLElement {
  #abort = new AbortController();
  /** @type {Array<{id:number, available:boolean, options:string[], price:string, compare:string|null, media:number|null}>} */
  #variants = [];
  /** @type {(typeof this.#variants)[number] | null} */
  #current = null;

  connectedCallback() {
    const { signal } = this.#abort;
    try {
      this.#variants = JSON.parse(this.querySelector('[data-pj-variants]')?.textContent ?? '[]');
    } catch {
      this.#variants = [];
    }

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.querySelector('#PjProductForm')?.addEventListener('submit', this.#onSubmit, { signal });

    for (const dialog of this.querySelectorAll('dialog')) {
      dialog.addEventListener(
        'click',
        (e) => {
          if (e.target === dialog) /** @type {HTMLDialogElement} */ (dialog).close();
        },
        { signal }
      );
    }

    const list = this.querySelector('[data-pj-gallery-list]');
    const counter = this.querySelector('[data-pj-gallery-index]');
    if (list && counter) {
      list.addEventListener(
        'scroll',
        () => {
          const index = Math.round(list.scrollLeft / Math.max(list.clientWidth, 1));
          counter.textContent = String(index + 1);
        },
        { passive: true, signal }
      );
    }

    this.#resolve({ updateUrl: false });

    // Arriving from a card's sold-out size, or back from a submitted notify form.
    const posted = new URLSearchParams(location.search).get('contact_posted') === 'true';
    if (posted || (location.hash === '#notify-me' && this.#current && !this.#current.available)) {
      this.#openNotify();
    }
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.dataset.pjOption !== undefined) {
      this.#resolve({ updateUrl: true });
    }
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const zoom = /** @type {HTMLElement | null} */ (target.closest('[data-pj-zoom]'));
    if (zoom) {
      const lightbox = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-pj-lightbox]'));
      lightbox?.showModal();
      lightbox?.querySelector(`[data-pj-lightbox-item="${zoom.dataset.pjZoom}"]`)?.scrollIntoView({ block: 'start' });
      return;
    }

    if (target.closest('[data-pj-size-guide-open]')) {
      /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-pj-size-guide]'))?.showModal();
      return;
    }

    if (target.closest('[data-pj-modal-close]')) {
      /** @type {HTMLDialogElement | null} */ (target.closest('dialog'))?.close();
    }
  };

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-pj-add-button]'));
    if (!button || !this.#current) return;

    if (!this.#current.available) {
      this.#openNotify();
      return;
    }

    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Adding…';
    this.#status('');

    const result = await addToCart(this, this.#current.id, 1, { productId: this.dataset.productId });

    button.removeAttribute('aria-busy');
    if (result.ok) {
      button.textContent = 'Added';
      this.#status('Added to bag');
      setTimeout(() => this.#resolve({ updateUrl: false }), 1400);
    } else {
      this.#resolve({ updateUrl: false });
      this.#status(result.message ?? 'Could not add to bag');
    }
  };

  /** @returns {(string | null)[]} */
  #selectedOptions() {
    const count = this.#variants[0]?.options.length ?? 0;
    const selected = [];
    for (let i = 0; i < count; i++) {
      const checked = /** @type {HTMLInputElement | null} */ (
        this.querySelector(`input[data-pj-option="${i}"]:checked, input[type="hidden"][data-pj-option="${i}"]`)
      );
      selected.push(checked ? checked.value : null);
    }
    return selected;
  }

  /** @param {{ updateUrl: boolean }} opts */
  #resolve({ updateUrl }) {
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-pj-add-button]'));
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-pj-variant-input]'));
    const dynamic = /** @type {HTMLElement | null} */ (this.querySelector('[data-pj-dynamic-checkout]'));
    if (!button || !input) return;

    // Single default variant
    if (this.#variants.length === 1 && this.querySelectorAll('[data-pj-option]').length === 0) {
      this.#current = this.#variants[0] ?? null;
    } else {
      const selected = this.#selectedOptions();

      // Selected value labels in the legends
      for (const group of this.querySelectorAll('[data-pj-option-group]')) {
        const index = Number(/** @type {HTMLElement} */ (group).dataset.pjOptionGroup);
        const label = group.querySelector('[data-pj-option-selected]');
        if (label) label.textContent = selected[index] ?? '';
      }

      if (selected.some((value) => value === null)) {
        this.#current = null;
        input.value = '';
        button.disabled = true;
        button.removeAttribute('data-mode');
        button.textContent = 'Select a size';
        if (dynamic) dynamic.hidden = true;
        return;
      }

      const norm = (/** @type {string | null} */ v) => (v ?? '').trim().toUpperCase();
      // Prefer an available match when mixed-case duplicates exist.
      const matches = this.#variants.filter((v) => v.options.every((value, i) => norm(value) === norm(selected[i])));
      this.#current = matches.find((v) => v.available) ?? matches[0] ?? null;
    }

    const variant = this.#current;
    if (!variant) {
      input.value = '';
      button.disabled = true;
      button.removeAttribute('data-mode');
      button.textContent = 'Unavailable';
      if (dynamic) dynamic.hidden = true;
      return;
    }

    input.value = String(variant.id);
    button.disabled = false;
    button.dataset.mode = variant.available ? 'add' : 'notify';
    button.textContent = variant.available ? 'Add to bag' : 'Notify me';
    if (dynamic) dynamic.hidden = !variant.available;

    const current = this.querySelector('[data-pj-price-current]');
    const compare = /** @type {HTMLElement | null} */ (this.querySelector('[data-pj-price-compare]'));
    if (current) current.textContent = variant.price;
    if (compare) {
      compare.textContent = variant.compare ?? '';
      compare.hidden = !variant.compare;
    }

    if (updateUrl) {
      const url = new URL(location.href);
      url.searchParams.set('variant', String(variant.id));
      url.hash = '';
      history.replaceState(history.state, '', url);
      this.#showVariantMedia(variant.media);
    }
  }

  /** On the mobile carousel, scroll to the variant's image. @param {number | null} mediaId */
  #showVariantMedia(mediaId) {
    if (!mediaId || window.matchMedia('(min-width: 990px)').matches) return;
    const list = this.querySelector('[data-pj-gallery-list]');
    const item = /** @type {HTMLElement | null} */ (this.querySelector(`[data-media-id="${mediaId}"]`));
    if (list && item) list.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
  }

  #openNotify() {
    // Klaviyo's back-in-stock embed may render its own trigger on the page.
    const klaviyoTrigger = /** @type {HTMLElement | null} */ (
      document.querySelector('.klaviyo-bis-trigger, [data-klaviyo-bis-trigger]')
    );
    if (klaviyoTrigger) {
      klaviyoTrigger.click();
      return;
    }

    const dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-pj-notify]'));
    if (!dialog) return;

    const variant = this.#current;
    const sizeLabel = this.#selectedOptions().filter(Boolean).join(' / ');
    const size = dialog.querySelector('[data-pj-notify-size]');
    if (size) size.textContent = sizeLabel;
    const body = /** @type {HTMLInputElement | null} */ (dialog.querySelector('[data-pj-notify-body]'));
    if (body && variant) {
      const title = this.querySelector('.pj-product__title')?.textContent?.trim() ?? '';
      body.value = `Back in stock request: ${title} — ${sizeLabel} (variant ${variant.id}) ${location.origin}${this.dataset.url}?variant=${variant.id}`;
    }
    dialog.showModal();
  }

  /** @param {string} message */
  #status(message) {
    const el = this.querySelector('[data-pj-status]');
    if (el) el.textContent = message;
  }
}

if (!customElements.get('pj-product')) customElements.define('pj-product', PjProduct);
